import type { ComponentType, SVGProps } from "react";

import { BOLD } from "./icons.generated";

// The one icon set for landing, /app and admin: Phosphor, Bold weight, so every
// icon has the same stroke (minimalist-ui design rules). Only the Bold drawing
// of each icon is shipped (icons.generated.tsx, from scripts/gen-icons.mjs):
// Phosphor's own components carry all six weights, which made the icons the
// biggest download on every page (75 KB on /signin, F203). No React context,
// so these work in server and client components alike.
// Import icons from here, never from @phosphor-icons/react directly. To add
// one: a line like the ones below with Phosphor's name for it, then
// `pnpm --filter @repo/ui gen:icons` (TypeScript flags a name not generated yet).

/** Phosphor's icon props: an <svg>'s, plus size, color, mirrored and alt. */
export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "ref"> {
  size?: number | string;
  color?: string;
  mirrored?: boolean;
  alt?: string;
}

export type AppIcon = ComponentType<IconProps>;

// The same <svg> Phosphor draws; size 24 matches the old lucide default
// (Phosphor's own default is 1em). Marked pure below, so unused icons can drop.
function bold(name: keyof typeof BOLD): AppIcon {
  const BoldIcon = ({ size = 24, color = "currentColor", mirrored = false, alt, children, ...rest }: IconProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      fill={color}
      viewBox="0 0 256 256"
      transform={mirrored ? "scale(-1, 1)" : undefined}
      aria-hidden
      {...rest}
    >
      {alt ? <title>{alt}</title> : null}
      {children}
      {BOLD[name]}
    </svg>
  );
  BoldIcon.displayName = name;
  return BoldIcon;
}

export const ArrowsDownUp = /* @__PURE__ */ bold("ArrowsDownUp");
export const Article = /* @__PURE__ */ bold("Article");
export const CaretDown = /* @__PURE__ */ bold("CaretDown");
export const CaretLeft = /* @__PURE__ */ bold("CaretLeft");
export const CaretRight = /* @__PURE__ */ bold("CaretRight");
export const CaretUp = /* @__PURE__ */ bold("CaretUp");
export const ChatCircle = /* @__PURE__ */ bold("ChatCircle");
export const Check = /* @__PURE__ */ bold("Check");
export const Clock = /* @__PURE__ */ bold("Clock");
export const Coins = /* @__PURE__ */ bold("Coins");
export const Copy = /* @__PURE__ */ bold("Copy");
export const CreditCard = /* @__PURE__ */ bold("CreditCard");
export const Diamond = /* @__PURE__ */ bold("Diamond");
export const House = /* @__PURE__ */ bold("House");
export const ImageIcon = /* @__PURE__ */ bold("Image");
export const Info = /* @__PURE__ */ bold("Info");
export const InstagramLogo = /* @__PURE__ */ bold("InstagramLogo");
export const Lightbulb = /* @__PURE__ */ bold("Lightbulb");
export const LinkedinLogo = /* @__PURE__ */ bold("LinkedinLogo");
export const List = /* @__PURE__ */ bold("List");
export const ListBullets = /* @__PURE__ */ bold("ListBullets");
export const MapPin = /* @__PURE__ */ bold("MapPin");
export const Moon = /* @__PURE__ */ bold("Moon");
export const Package = /* @__PURE__ */ bold("Package");
export const Phone = /* @__PURE__ */ bold("Phone");
export const ShareNetwork = /* @__PURE__ */ bold("ShareNetwork");
export const ShieldCheck = /* @__PURE__ */ bold("ShieldCheck");
export const ShieldWarning = /* @__PURE__ */ bold("ShieldWarning");
export const ShoppingCart = /* @__PURE__ */ bold("ShoppingCart");
export const SignOut = /* @__PURE__ */ bold("SignOut");
export const SquaresFour = /* @__PURE__ */ bold("SquaresFour");
export const Storefront = /* @__PURE__ */ bold("Storefront");
export const Sun = /* @__PURE__ */ bold("Sun");
export const TelegramLogo = /* @__PURE__ */ bold("TelegramLogo");
export const TrendDown = /* @__PURE__ */ bold("TrendDown");
export const TrendUp = /* @__PURE__ */ bold("TrendUp");
export const User = /* @__PURE__ */ bold("User");
export const WarningCircle = /* @__PURE__ */ bold("WarningCircle");
export const WhatsappLogo = /* @__PURE__ */ bold("WhatsappLogo");
export const XLogo = /* @__PURE__ */ bold("XLogo");
export const YoutubeLogo = /* @__PURE__ */ bold("YoutubeLogo");
export const ArrowRight = /* @__PURE__ */ bold("ArrowRight");
export const CheckCircle = /* @__PURE__ */ bold("CheckCircle");
export const EnvelopeSimple = /* @__PURE__ */ bold("EnvelopeSimple");
export const SealCheck = /* @__PURE__ */ bold("SealCheck");
export const Truck = /* @__PURE__ */ bold("Truck");
export const DeviceMobile = /* @__PURE__ */ bold("DeviceMobile");
export const Desktop = /* @__PURE__ */ bold("Desktop");
export const DeviceTablet = /* @__PURE__ */ bold("DeviceTablet");
export const LockKey = /* @__PURE__ */ bold("LockKey");
export const Eye = /* @__PURE__ */ bold("Eye");
export const EyeSlash = /* @__PURE__ */ bold("EyeSlash");
export const Ticket = /* @__PURE__ */ bold("Ticket");
export const SealPercent = /* @__PURE__ */ bold("SealPercent");
export const Gift = /* @__PURE__ */ bold("Gift");
export const PencilSimple = /* @__PURE__ */ bold("PencilSimple");
export const CurrencyCircleDollar = /* @__PURE__ */ bold("CurrencyCircleDollar");
export const Flask = /* @__PURE__ */ bold("Flask");
export const IdentificationCard = /* @__PURE__ */ bold("IdentificationCard");
export const Cloud = /* @__PURE__ */ bold("Cloud");
export const Plugs = /* @__PURE__ */ bold("Plugs");
export const PaperPlaneRight = /* @__PURE__ */ bold("PaperPlaneRight");
export const TextB = /* @__PURE__ */ bold("TextB");
export const TextItalic = /* @__PURE__ */ bold("TextItalic");
export const TextStrikethrough = /* @__PURE__ */ bold("TextStrikethrough");
export const ListNumbers = /* @__PURE__ */ bold("ListNumbers");
export const Quotes = /* @__PURE__ */ bold("Quotes");
export const LinkSimple = /* @__PURE__ */ bold("LinkSimple");
export const LinkBreak = /* @__PURE__ */ bold("LinkBreak");
export const ArrowCounterClockwise = /* @__PURE__ */ bold("ArrowCounterClockwise");
export const Paperclip = /* @__PURE__ */ bold("Paperclip");
export const FilePdf = /* @__PURE__ */ bold("FilePdf");
export const ArrowBendUpLeft = /* @__PURE__ */ bold("ArrowBendUpLeft");
export const NotePencil = /* @__PURE__ */ bold("NotePencil");
export const ShareFat = /* @__PURE__ */ bold("ShareFat");
export const HandGrabbing = /* @__PURE__ */ bold("HandGrabbing");
export const UserSwitch = /* @__PURE__ */ bold("UserSwitch");
export const LockSimple = /* @__PURE__ */ bold("LockSimple");
export const LockSimpleOpen = /* @__PURE__ */ bold("LockSimpleOpen");
export const GitMerge = /* @__PURE__ */ bold("GitMerge");
export const ArrowUUpLeft = /* @__PURE__ */ bold("ArrowUUpLeft");
export const ChatText = /* @__PURE__ */ bold("ChatText");
export const ChatsCircle = /* @__PURE__ */ bold("ChatsCircle");
export const Buildings = /* @__PURE__ */ bold("Buildings");
export const Bank = /* @__PURE__ */ bold("Bank");
export const Warehouse = /* @__PURE__ */ bold("Warehouse");
export const Barcode = /* @__PURE__ */ bold("Barcode");
export const MagnifyingGlass = /* @__PURE__ */ bold("MagnifyingGlass");
export const Plus = /* @__PURE__ */ bold("Plus");
export const Minus = /* @__PURE__ */ bold("Minus");
export const ArrowsLeftRight = /* @__PURE__ */ bold("ArrowsLeftRight");
export const Key = /* @__PURE__ */ bold("Key");
export const Handshake = /* @__PURE__ */ bold("Handshake");
export const ArrowLeft = /* @__PURE__ */ bold("ArrowLeft");
export const X = /* @__PURE__ */ bold("X");
export const Gear = /* @__PURE__ */ bold("Gear");
export const Globe = /* @__PURE__ */ bold("Globe");
export const Wallet = /* @__PURE__ */ bold("Wallet");
export const ClipboardText = /* @__PURE__ */ bold("ClipboardText");
export const Scales = /* @__PURE__ */ bold("Scales");
export const UsersThree = /* @__PURE__ */ bold("UsersThree");
export const Camera = /* @__PURE__ */ bold("Camera");
export const Question = /* @__PURE__ */ bold("Question");
export const ArrowCircleDown = /* @__PURE__ */ bold("ArrowCircleDown");
export const ArrowCircleUp = /* @__PURE__ */ bold("ArrowCircleUp");
export const ClockCounterClockwise = /* @__PURE__ */ bold("ClockCounterClockwise");
export const Export = /* @__PURE__ */ bold("Export");
export const PlusSquare = /* @__PURE__ */ bold("PlusSquare");
export const DownloadSimple = /* @__PURE__ */ bold("DownloadSimple");
export const ArrowsClockwise = /* @__PURE__ */ bold("ArrowsClockwise");
export const Bell = /* @__PURE__ */ bold("Bell");
export const Users = /* @__PURE__ */ bold("Users");
export const Prohibit = /* @__PURE__ */ bold("Prohibit");
export const Palette = /* @__PURE__ */ bold("Palette");
export const Receipt = /* @__PURE__ */ bold("Receipt");
export const Moped = /* @__PURE__ */ bold("Moped");
